import { snapshotHistory } from "../app/shell.js";
import { esc, fmtMoney } from "./format.js";
import { mountChart } from "../charts/index.js";

let cashChart = null;

let marketShareChart = null;

// ---------------------------------------------------------------- Candlestick chart (Canvas)
// Renders real OHLC candles from the game's monthly share-price series.
// The engine only tracks one closing price per month, so open/high/low are
// derived deterministically from the two adjacent closes (seeded by month
// number, so the same game state always draws the same candle) — this is
// a *display* technique, not fabricated market data: every candle's close
// is still the real simulated share price for that month.
function seededRand(seed) {
  let x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

function deriveOHLC(priceHistory) {
  return priceHistory.map((p, i) => {
    const prevClose = i > 0 ? priceHistory[i - 1].share_price : p.share_price;
    const close = p.share_price;
    const open = prevClose;
    const spread = Math.max(Math.abs(close - open), close * 0.015);
    const r1 = seededRand(p.month * 7.13);
    const r2 = seededRand(p.month * 3.71 + 1);
    const high = Math.max(open, close) + spread * (0.3 + r1 * 0.5);
    const low = Math.max(0.01, Math.min(open, close) - spread * (0.3 + r2 * 0.5));
    return { month: p.month, open, close, high, low };
  });
}

/** Share price as monthly candles, through the chart kit (3D in 3D mode). */
let candleChart = null;
function drawCandlestickChart(el, priceHistory) {
  if (!el || !priceHistory || priceHistory.length < 2) return;
  const candles = deriveOHLC(priceHistory);
  const spec = {
    kind: "candlestick", title: "Share price, monthly candles", unit: "$",
    x: candles.map((c) => `M${c.month}`),
    series: [{ key: "price", label: "Share price", role: "equity", values: candles.map((c) => ({ o: c.open, h: c.high, l: c.low, c: c.close })) }],
  };
  if (candleChart && candleChart.el === el) { candleChart.chart.update(spec); return; }
  if (candleChart) candleChart.chart.dispose();
  candleChart = { el, chart: mountChart(el, spec, { height: 220 }) };
}


// ---------------------------------------------------------------- chart

/** Cash and revenue by month (Overview), through the chart kit. */
function drawCashChart() {
  const el = document.getElementById("cash-chart");
  if (!el) return;
  const spec = {
    kind: "area", title: "Cash and revenue by month", unit: "$",
    x: snapshotHistory.map((s) => `M${s.month}`),
    series: [
      { key: "cash", label: "Cash", role: "cash", values: snapshotHistory.map((s) => s.balance_sheet.CASH) },
      { key: "revenue", label: "Revenue", role: "revenue", values: snapshotHistory.map((s) => s.income_statement.revenue) },
    ],
  };
  if (cashChart && cashChart.el === el) { cashChart.chart.update(spec); return; }
  if (cashChart) cashChart.chart.dispose();
  cashChart = { el, chart: mountChart(el, spec) };
}

/** You vs known rivals, estimated annual revenue (Overview donut). */
function drawMarketShareChart(monthlyRevenue, competitors) {
  const el = document.getElementById("market-share-chart");
  if (!el) return;
  const active = competitors.filter((c) => c.status !== "Bankrupt");
  const spec = {
    kind: "donut", title: "You vs. known rivals: estimated annual revenue", unit: "$",
    x: ["You", ...active.map((c) => c.name)],
    series: [{ key: "revenue", label: "Est. annual revenue", values: [monthlyRevenue * 12, ...active.map((c) => c.monthly_revenue * 12)] }],
  };
  if (marketShareChart && marketShareChart.el === el) { marketShareChart.chart.update(spec); return; }
  if (marketShareChart) marketShareChart.chart.dispose();
  marketShareChart = { el, chart: mountChart(el, spec) };
}

export { drawCandlestickChart, drawCashChart, drawMarketShareChart };
